import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getStoreBySlug,
  getProducts,
  getStoreTournaments,
  getStoreProfileInfo,
  getStoreSchedule,
  getStoreHours,
  getStorePaymentMethods,
  getStoreShippingMethods,
  isStoreOpen,
  getStoreGames,
  getCardgames,
  getMyStore,
} from "../../services/supabaseService";
import {
  ProductType,
  Product,
  StoreEvent,
  Store,
  GameType,
  PaymentMethod,
  ShippingMethod,
  isTicketProduct,
} from "../../types";
import { useAuth } from "../../components/AuthProvider";
import { useNotification } from "../../components/member/NotificationProvider";
import { getGameIcon } from "../../constants/games";

interface StoreProfileProps {
  onAddToCart: (product: Product) => void;
}

type TabType = "home" | "agenda" | "events" | "products";

const formatInstagramUrl = (handleOrUrl?: string | null): string => {
  if (!handleOrUrl) return '';
  let cleaned = String(handleOrUrl).trim();
  if (!cleaned) return '';

  // Remove trailing slashes
  cleaned = cleaned.replace(/\/+$/, '');

  // Handle cases where full or partial URL containing instagram.com is passed
  if (cleaned.includes('instagram.com/')) {
    const parts = cleaned.split('instagram.com/');
    const rawUsername = parts[parts.length - 1];
    const username = rawUsername.replace(/^@/, '').split('/')[0].split('?')[0].split('#')[0];
    return username ? `https://www.instagram.com/${username}/` : '';
  }

  // Handle handle/username format e.g. @sadiesink_ or sadiesink_
  cleaned = cleaned.replace(/^@/, '').split('/')[0].split('?')[0].split('#')[0];
  return cleaned ? `https://www.instagram.com/${cleaned}/` : '';
};

const safeString = (val: any): string | null => {
  if (val === null || val === undefined) return null;
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed || trimmed === "[object Object]") return null;
    return trimmed;
  }
  if (typeof val === "number" || typeof val === "boolean") {
    return String(val);
  }
  if (typeof val === "object") {
    if (Array.isArray(val)) {
      const parts = val.map(safeString).filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : null;
    }
    // Check common nested property keys
    for (const key of ["text", "desc", "description", "value", "phone", "telefone", "whatsapp", "email", "about", "sobre", "horario", "address", "street", "0"]) {
      if (val[key] !== undefined && val[key] !== null) {
        const res = safeString(val[key]);
        if (res) return res;
      }
    }
    const parts = Object.values(val).map(safeString).filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  }
  return null;
};

const extractFromContactOrSocial = (data: any, keysToSearch: string[]): string | null => {
  if (!data) return null;

  let parsed = data;
  if (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    } else {
      return trimmed;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;

  // 1. Check direct keys
  for (const key of keysToSearch) {
    if (parsed[key] !== undefined && parsed[key] !== null) {
      const val = safeString(parsed[key]);
      if (val) return val;
    }
  }

  // 2. Check child objects or arrays
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const found = extractFromContactOrSocial(item, keysToSearch);
      if (found) return found;
    }
  } else if (typeof parsed === "object") {
    for (const childKey of Object.keys(parsed)) {
      const val = parsed[childKey];
      if (val) {
        const found = extractFromContactOrSocial(val, keysToSearch);
        if (found) return found;
      }
    }
  }

  return null;
};

const DAY_ORDER = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const getGameIconClass = (name: string): string => getGameIcon(name);

export const StoreProfile: React.FC<StoreProfileProps> = ({ onAddToCart }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isOffline, user } = useAuth();
  const { showNotification } = useNotification();
  const isLojista = user?.role_id === 6 || user?.role_id === 1;
  const [myStore, setMyStore] = useState<any>(null);

  useEffect(() => {
    if (user && isLojista) {
      getMyStore().then(s => setMyStore(s));
    }
  }, [user, isLojista]);

  const [store, setStore] = useState<Store | null>(null);
  const isOwnStore = useMemo(() => {
    if (!isLojista || !myStore || !store) return false;
    return Number(myStore.id) === Number(store.id) || myStore.slug === store.slug;
  }, [isLojista, myStore, store]);
  const [storeEvents, setStoreEvents] = useState<StoreEvent[]>([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [storeGames, setStoreGames] = useState<any[]>([]);
  const [stats, setStats] = useState<{
    wishlist_size: number;
    stock_size: number;
    offers_size: number;
    is_open?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>("home");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      let storeData = null;
      if (id) {
        storeData = await getStoreBySlug(id);

        // Fetch stats if possible using slug and verify if store is open
        let isOpen = false;
        if (storeData) {
          try {
            isOpen = await isStoreOpen(storeData.id);
          } catch (e) {
            console.error("Error fetching store open status:", e);
          }
        }
        setStats({
          wishlist_size: 0,
          stock_size: 0,
          offers_size: 0,
          is_open: isOpen,
        });
      }

      if (storeData) {
        // Try to parse JSON fields if they are strings
        const tryParse = (val: any) => {
          if (typeof val === "string") {
            try {
              return JSON.parse(val);
            } catch (e) {
              return val;
            }
          }
          return val;
        };

        const contato = tryParse(storeData.contact || storeData.contato);
        const redesSociais = tryParse(
          storeData.social_links || storeData.redes_sociais,
        );

        const extractedPhone =
          extractFromContactOrSocial(contato, ["phone", "telefone", "celular", "tel"]) ||
          storeData.phone ||
          storeData.telefone;

        const extractedWhatsapp =
          extractFromContactOrSocial(contato, ["whatsapp", "wa"]) ||
          extractFromContactOrSocial(redesSociais, ["whatsapp", "wa"]) ||
          storeData.whatsapp;

        const extractedEmail =
          extractFromContactOrSocial(contato, ["email"]) ||
          storeData.email;

        const extractedInstagram = formatInstagramUrl(
          extractFromContactOrSocial(redesSociais, ["instagram", "insta"]) ||
          extractFromContactOrSocial(contato, ["instagram", "insta"]) ||
          storeData.instagram
        );

        const extractedDiscord =
          extractFromContactOrSocial(redesSociais, ["discord"]) ||
          extractFromContactOrSocial(contato, ["discord"]) ||
          storeData.discord;

        const extractedSite =
          extractFromContactOrSocial(redesSociais, ["site", "website", "url"]) ||
          extractFromContactOrSocial(contato, ["site", "website", "url"]) ||
          storeData.site;

        // Combine address fields for a full location string
        const addrStr = safeString(storeData.address || storeData.endereco);
        const neighStr = safeString(storeData.neighborhood || storeData.bairro);
        const cityStr = safeString(storeData.city || storeData.cidade);
        const stateStr = safeString(storeData.estado) || "";

        const fullLocation = addrStr
          ? `${addrStr}${neighStr ? `, ${neighStr}` : ""}${cityStr ? ` - ${cityStr}, ${stateStr}` : ""}`
          : safeString(storeData.location) || "Localização não informada";

        // Map database fields to the Store interface
        const mappedStore: Store = {
          ...storeData,
          id: String(storeData.id),
          name: safeString(storeData.name) || "Loja",
          logo: typeof storeData.logo === "string" ? storeData.logo : undefined,
          location: fullLocation,
          isPartner: !!storeData.is_partner || !!storeData.isPartner,
          sales_enabled: !!storeData.sales_enabled,
          phone: extractedPhone || safeString(storeData.phone) || safeString(storeData.telefone) || undefined,
          whatsapp: extractedWhatsapp || safeString(storeData.whatsapp) || undefined,
          email: extractedEmail || safeString(storeData.email) || undefined,
          instagram: extractedInstagram || undefined,
          discord: safeString(extractedDiscord) || undefined,
          site: safeString(extractedSite) || undefined,
          opening_hours: safeString(storeData.opening_hours) || safeString(storeData.horarios) || undefined,
          about: safeString(storeData.about) || safeString(storeData.sobre) || undefined,
          schedule: Array.isArray(storeData.schedule) ? storeData.schedule : [],
        };

        // Fetch real schedule from backend
        const realSchedule = await getStoreSchedule(String(mappedStore.id));
        if (realSchedule && realSchedule.length > 0) {
          mappedStore.schedule = realSchedule.map((s: any) => ({
            day: s.dia,
            game: (s.game_name || s.jogo) as GameType,
            time: s.horario,
            fee: s.valor_insc ? `R$ ${s.valor_insc}` : "Gratuito",
          }));
        }

        // Fetch real hours from backend
        const realHours = await getStoreHours(String(mappedStore.id));
        if (realHours && realHours.length > 0) {
          mappedStore.businessHours = realHours;
        }

        // Fetch real payment methods
        try {
          const realPaymentMethods = await getStorePaymentMethods(
            String(mappedStore.id),
          );
          if (realPaymentMethods && realPaymentMethods.length > 0) {
            setPaymentMethods(realPaymentMethods);
          }
        } catch (paymentErr) {
          console.error("Error loading store payment methods:", paymentErr);
        }

        // Fetch real shipping methods
        try {
          const realShippingMethods = await getStoreShippingMethods(
            String(mappedStore.id),
          );
          if (realShippingMethods && realShippingMethods.length > 0) {
            setShippingMethods(realShippingMethods);
          }
        } catch (shippingErr) {
          console.error("Error loading store shipping methods:", shippingErr);
        }

        setStore(mappedStore);

        // Fetch store games
        try {
          const storeGameIds = await getStoreGames(String(mappedStore.id));
          const allGames = await getCardgames();
          const activeGames = allGames.filter((g: any) =>
            storeGameIds.includes(g.id),
          );
          setStoreGames(activeGames || []);
        } catch (e) {
          console.error("Error loading store games:", e);
        }

        // Fetch real tournaments/events for this store
        const tournamentData = await getStoreTournaments(
          String(id || mappedStore.id),
        );
        if (tournamentData && Array.isArray(tournamentData)) {
          const mappedEvents: StoreEvent[] = tournamentData.map(
            (e: any, idx: number) => ({
              id: String(e.id),
              name: e.name,
              date: e.start_date
                ? new Date(e.start_date).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "A combinar",
              game: (e.games?.name || "TCG") as GameType,
              price: e.price || 0,
              totalSpots: e.max_players || 0,
              filledSpots: 0,
              type: e.status === "scheduled" ? "Special" : "Tournament",
              description: e.description || "",
              imageUrl:
                e.imageUrl ||
                e.image_url ||
                "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400",
              isHighlighted: idx === 0,
            }),
          );
          setStoreEvents(mappedEvents);
        }
      } else {
        setStore(null);
        setStoreEvents([]);
        setPaymentMethods([]);
        setShippingMethods([]);
      }

      getProducts((data) => {
        setAllProducts(data);
        setLoading(false);
      });
    };

    fetchData();
  }, [id]);

  const mappedStoreProducts = useMemo(() => {
    if (!store) return [];
    return allProducts
      .filter((p) => {
        if (p.is_available === false) return false;
        // Ingressos nunca aparecem na página da loja
        if (isTicketProduct(p)) return false;

        const stockItems = (Array.isArray(p.store_products)
          ? p.store_products
          : []).filter((si: any) => !si.deleted_at);
        return stockItems.some(
          (si: any) =>
            (String(si.store?.id) === String(store.id) ||
              si.store?.slug === store.slug) &&
            ((si.quantity || 0) - (si.reserved_quantity || 0)) > 0,
        );
      })
      .map((p) => {
        const stockEntry = (p.store_products || []).find(
          (si: any) =>
            !si.deleted_at &&
            (String(si.store?.id) === String(store.id) ||
              si.store?.slug === store.slug) &&
            ((si.quantity || 0) - (si.reserved_quantity || 0)) > 0,
        );
        return {
          ...p,
          id: p.id,
          imageUrl:
            p.image_url ||
            p.imageUrl ||
            "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400",
          name: p.beauty_name || p.name,
          price: stockEntry?.store_price || p.mspr || p.msrp || 0,
          stock: stockEntry ? Math.max(0, (stockEntry.quantity || 0) - (stockEntry.reserved_quantity || 0)) : 0,
          storeId: store.id,
          storeName: store.name,
          isOfficialPartner: store.isPartner,
          game:
            p.games?.name ||
            (Array.isArray(p.games) ? p.games[0]?.name : "TCG"),
          type:
            p.product_types?.name ||
            (Array.isArray(p.product_types)
              ? p.product_types[0]?.name
              : "Produto"),
        };
      });
  }, [allProducts, store]);

  const totalPages = Math.ceil(mappedStoreProducts.length / itemsPerPage);

  const paginatedStoreProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return mappedStoreProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [mappedStoreProducts, currentPage]);

  const sortedSchedule = useMemo(() => {
    if (!store || !store.schedule) return [];
    return [...store.schedule].sort((a, b) => {
      return DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day);
    });
  }, [store]);

  const highlightedEvent = useMemo(() => {
    return storeEvents.find((e) => e.isHighlighted) || storeEvents[0];
  }, [storeEvents]);

  const shareStore = () => {
    if (navigator.share) {
      navigator
        .share({
          title: store?.name,
          text: `Confira a loja ${store?.name} no Cardumy!`,
          url: window.location.href,
        })
        .catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      showNotification("Link copiado para a área de transferência!", "success");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <i className="fas fa-store-slash text-6xl text-slate-800"></i>
        <h2 className="text-2xl font-bold text-slate-500">
          Loja não encontrada
        </h2>
        <Link to="/lojas" className="text-purple-400 hover:underline">
          Voltar para lista de lojas
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 md:space-y-8 animate-in fade-in duration-700 pb-20 md:pb-8">
      {/* Top Nav Buttons */}
      <div className="flex items-center justify-between">
        <Link
          to="/lojas"
          className="bg-slate-900/50 hover:bg-slate-800 text-slate-400 px-3 md:px-4 py-2 rounded-xl border border-slate-800 text-[10px] md:text-xs font-bold transition-all flex items-center space-x-2"
        >
          <i className="fas fa-arrow-left"></i>
          <span>Explorar Lojas</span>
        </Link>
        <div className="flex space-x-2">
          <button className="w-9 h-9 md:w-10 md:h-10 bg-slate-900/50 rounded-xl border border-slate-800 text-slate-500 hover:text-pink-500 transition-colors">
            <i className="fas fa-heart text-sm md:text-base"></i>
          </button>
          <button
            onClick={shareStore}
            className="w-9 h-9 md:w-10 md:h-10 bg-slate-900/50 rounded-xl border border-slate-800 text-slate-500 hover:text-purple-400 transition-colors"
          >
            <i className="fas fa-share-nodes text-sm md:text-base"></i>
          </button>
        </div>
      </div>

      {/* Header Banner & Info Wrapper */}
      <div className="relative mb-6 md:mb-16">
        {/* Banner Cover */}
        <div className="relative rounded-2xl md:rounded-[40px] overflow-hidden border border-slate-800 shadow-2xl h-36 md:h-[300px]">
          <img
            src={
              store.logo ||
              "https://images.unsplash.com/photo-1606167668584-78701c57f13d?auto=format&fit=crop&q=80&w=1200"
            }
            className="w-full h-full object-cover brightness-[0.4]"
            alt="Store Banner"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent"></div>
        </div>

        {/* Banner Overlay Info */}
        <div className="relative mt-[-40px] md:mt-0 md:absolute md:bottom-6 left-4 md:left-10 right-4 md:right-10 flex flex-col md:flex-row items-center md:items-end space-y-3 md:space-y-0 md:space-x-6 px-3 md:px-0 text-center md:text-left z-10 w-full max-w-[calc(100%-2rem)] md:max-w-none mx-auto">
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 md:w-32 md:h-32 rounded-2xl md:rounded-3xl border-4 border-slate-950 bg-slate-900 shadow-xl overflow-hidden flex items-center justify-center mx-auto ring-1 ring-purple-500/30">
              <img
                src={
                  store.logo ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name || "")}`
                }
                className="w-full h-full object-contain p-2"
                alt={store.name}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="flex-1 space-y-3 md:space-y-4 pb-2 w-full">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4 justify-center md:justify-start">
              <h1 className="text-2xl md:text-5xl font-black tracking-tight text-white leading-none">
                {store.name}
              </h1>
              {store.isPartner && (
                <div className="group relative">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-950 text-[8px] md:text-[10px] font-black px-3 md:px-4 py-1 rounded-full uppercase tracking-widest shadow-lg flex items-center">
                    <i className="fas fa-crown mr-1 md:mr-2"></i>
                    Parceiro Cardumy
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3 md:space-y-4">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 md:gap-4">
                <div className="flex items-center space-x-2 text-white/90 text-xs md:text-sm font-bold justify-center">
                  <i className="fas fa-location-dot text-purple-400"></i>
                  <span>{store.location}</span>
                </div>
                {stats && stats.is_open !== undefined && (
                  <div
                    className={`flex items-center space-x-2 px-3 py-1 rounded-full border text-[8px] md:text-[10px] font-black uppercase tracking-widest ${
                      stats.is_open
                        ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400"
                        : "bg-rose-500/20 border-rose-500/30 text-rose-400"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${stats.is_open ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`}
                    ></span>
                    <span>{stats.is_open ? "Aberto Agora" : "Fechado"}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-3">
                <div className="flex items-center space-x-2 md:space-x-3 bg-slate-950/60 backdrop-blur-sm px-3 md:px-5 py-2 md:py-2.5 rounded-xl md:rounded-2xl border border-white/5 justify-center md:justify-start">
                  <span className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest mr-1">
                    Pagamento:
                  </span>
                  <div className="flex items-center flex-wrap gap-x-2 md:gap-x-4 gap-y-1">
                    {paymentMethods.length > 0 ? (
                      paymentMethods.map((method, idx) => {
                        const nameLower = method.name?.toLowerCase() || "";
                        const isPix =
                          method.type === "pix" ||
                          method.slug === "pix" ||
                          nameLower.includes("pix");
                        const isMoney =
                          nameLower.includes("dinheiro") ||
                          nameLower.includes("cash");
                        const isCard =
                          nameLower.includes("cartão") ||
                          nameLower.includes("card") ||
                          nameLower.includes("crédito") ||
                          nameLower.includes("débito");
                        let iconClass = "fa-credit-card";
                        if (isPix) iconClass = "fa-qrcode";
                        else if (isMoney) iconClass = "fa-money-bill-wave";
                        else if (isCard) iconClass = "fa-credit-card";
                        else if (
                          nameLower.includes("mercado") ||
                          nameLower.includes("mp")
                        )
                          iconClass = "fa-handshake";

                        return (
                          <span
                            key={method.id || idx}
                            className="flex items-center text-[9px] md:text-[11px] text-slate-200 font-bold"
                          >
                            <i
                              className={`fas ${iconClass} text-purple-400 mr-1.5 md:mr-2`}
                            ></i>
                            {method.name}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-[9px] md:text-[11px] text-slate-500 italic">
                        Nenhum
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 md:space-x-3 bg-slate-950/60 backdrop-blur-sm px-3 md:px-5 py-2 md:py-2.5 rounded-xl md:rounded-2xl border border-white/5 justify-center md:justify-start">
                  <span className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest mr-1">
                    Logística:
                  </span>
                  <div className="flex items-center flex-wrap gap-x-2 md:gap-x-4 gap-y-1">
                    {shippingMethods.length > 0 ? (
                      shippingMethods.map((method, idx) => {
                        const nameLower = method.name?.toLowerCase() || "";
                        const isRetirada =
                          nameLower.includes("retirada") ||
                          nameLower.includes("balcão") ||
                          nameLower.includes("loja") ||
                          nameLower.includes("local");
                        const iconClass = isRetirada
                          ? "fa-store"
                          : "fa-truck-fast";
                        const textClass = isRetirada
                          ? "text-emerald-400"
                          : "text-slate-200";
                        return (
                          <span
                            key={method.id || idx}
                            className={`flex items-center text-[9px] md:text-[11px] ${textClass} font-bold`}
                          >
                            <i
                              className={`fas ${iconClass} mr-1.5 md:mr-2`}
                            ></i>
                            {method.name}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-[9px] md:text-[11px] text-slate-500 italic">
                        Nenhum
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
        <div className="lg:col-span-8 space-y-8 md:space-y-12">
          {/* Navigation Tabs */}
          <div className="flex items-center space-x-1 bg-slate-900/30 p-1 rounded-xl md:rounded-2xl border border-slate-800/50 overflow-x-auto scrollbar-hide">
            {(["home", "agenda", "events", "products"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 min-w-[100px] py-2 md:py-3 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === tab
                    ? "bg-purple-600 text-white shadow-lg"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                {tab === "home"
                  ? "Destaques"
                  : tab === "agenda"
                    ? "Agenda Semanal"
                    : tab === "events"
                      ? "Eventos"
                      : "Produtos"}
              </button>
            ))}
          </div>

          {activeTab === "home" && (
            <div className="space-y-8 md:space-y-12 animate-in fade-in duration-500">
              {/* Highlights Section */}
              <section className="space-y-4 md:space-y-6">
                <div className="relative overflow-hidden bg-slate-900/40 border border-slate-800 rounded-[32px] p-8 md:p-10">
                  <div className="relative z-10 space-y-4">
                    <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight">
                      Sobre a Loja
                    </h3>
                    <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                      {safeString(store.about) ||
                        "Esta loja ainda não preencheu suas informações."}
                    </p>
                  </div>
                  <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-600/5 rounded-full blur-[100px]"></div>
                </div>

                {highlightedEvent && store.isPartner && (
                  <section className="relative rounded-2xl md:rounded-[32px] overflow-hidden bg-gradient-to-br from-slate-900 to-indigo-950/30 border border-purple-500/20 p-6 md:p-8 shadow-2xl">
                    <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                      <img
                        src={
                          highlightedEvent.imageUrl ||
                          "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=300"
                        }
                        className="w-40 h-40 md:w-48 md:h-48 object-cover rounded-2xl shadow-xl border border-white/5"
                        alt="Promo"
                      />
                      <div className="flex-1 space-y-3 md:space-y-4">
                        <div className="inline-flex items-center space-x-2 bg-purple-500/20 px-3 py-1 rounded-full border border-purple-500/30">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                          <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-purple-400">
                            Destaque do Mês
                          </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-black text-white">
                          {highlightedEvent.name}
                        </h3>
                        <p className="text-slate-400 text-xs md:text-sm leading-relaxed">
                          {safeString(highlightedEvent.description)}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
                          <Link
                            to={`/evento/${highlightedEvent.id}`}
                            className="w-full sm:w-auto font-black px-10 py-3.5 rounded-xl shadow-lg active:scale-95 text-sm transition-all bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 inline-flex items-center justify-center space-x-2"
                          >
                            <i className="fas fa-trophy mr-1 text-xs"></i>
                            <span>Ver Detalhes do Evento</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </section>
                )}
              </section>
            </div>
          )}

          {activeTab === "agenda" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 animate-in fade-in duration-500">
              {/* Business Hours */}
              <div className="space-y-6">
                <h3 className="text-lg md:text-xl font-black text-white flex items-center">
                  <span className="w-1 h-5 md:w-1.5 md:h-6 bg-emerald-600 rounded-full mr-3"></span>
                  Horário de Funcionamento
                </h3>
                <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-4">
                  {store.businessHours && store.businessHours.length > 0 ? (
                    DAY_ORDER.map((day, idx) => {
                      const hoursForDay = store.businessHours?.filter(
                        (h) => h.day_of_week === idx,
                      );
                      const isClosed =
                        !hoursForDay ||
                        hoursForDay.length === 0 ||
                        hoursForDay.every((h) => h.is_closed);

                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-1 border-b border-white/5 last:border-0"
                        >
                          <span
                            className={`text-xs md:text-sm font-bold ${idx === new Date().getDay() ? "text-purple-400" : "text-slate-400"}`}
                          >
                            {day}
                          </span>
                          <div className="text-right">
                            {isClosed ? (
                              <span className="text-xs md:text-sm font-black text-rose-500/80 uppercase">
                                Fechado
                              </span>
                            ) : (
                              <div className="space-y-1">
                                {hoursForDay?.map((h, hidx) => (
                                  <p
                                    key={hidx}
                                    className="text-xs md:text-sm font-black text-white"
                                  >
                                    {h.open_time?.substring(0, 5) || "--:--"} -{" "}
                                    {h.close_time?.substring(0, 5) || "--:--"}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-500 text-sm italic py-4">
                      Horário de funcionamento não informado.
                    </p>
                  )}
                </div>
              </div>

              {/* Weekly Events */}
              <div className="space-y-6">
                <h3 className="text-lg md:text-xl font-black text-white flex items-center">
                  <span className="w-1 h-5 md:w-1.5 md:h-6 bg-purple-600 rounded-full mr-3"></span>
                  Torneios Semanais
                </h3>

                <div className="space-y-4">
                  {sortedSchedule.length > 0 ? (
                    sortedSchedule.map((t, i) => (
                      <div
                        key={i}
                        className="bg-slate-900/50 border border-slate-800 p-5 md:p-6 rounded-2xl flex items-center justify-between group hover:border-purple-500/30 transition-all"
                      >
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-[10px] uppercase tracking-tighter ${
                              t.day === "Domingo"
                                ? "bg-orange-500/20 text-orange-400"
                                : "bg-slate-800 text-slate-500"
                            }`}
                          >
                            {t.day?.substring(0, 3) || "???"}
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-white uppercase">
                              {t.game}
                            </h4>
                            <p className="text-[10px] text-slate-500 mt-1 flex items-center">
                              <i className="far fa-clock mr-1.5"></i>
                              {t.time}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-black text-emerald-400">
                            {t.fee || "Gratuito"}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-12 border border-dashed border-slate-800 rounded-3xl text-center text-slate-500 text-sm italic">
                      Sem torneios fixos na agenda no momento.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "events" && (
            <div className="space-y-6 animate-in fade-in duration-500">
              {!store.isPartner ? (
                <div className="p-8 md:p-12 rounded-[32px] bg-slate-900/30 border border-slate-800/80 text-center flex flex-col items-center justify-center space-y-6">
                  <div className="w-16 h-16 rounded-2xl bg-purple-600/10 text-purple-400 flex items-center justify-center border border-purple-500/10">
                    <i className="fas fa-shield-halved text-3xl"></i>
                  </div>
                  <div className="max-w-md space-y-3">
                    <h4 className="text-base font-black text-white uppercase tracking-wider">
                      Apenas Lojas Parceiras
                    </h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Apenas as lojas parceiras oficiais da Cardumy possuem
                      acesso ao gerenciamento de eventos. Isso garante
                      informações rigorosamente confiáveis, seguras e
                      atualizadas em nosso sistema.
                    </p>
                  </div>
                  {store.whatsapp && (
                    <a
                      href={`https://wa.me/${store.whatsapp}?text=Ol%C3%A1!%20Vi%20sua%20loja%20no%20Cardumy%20e%20gostaria%20de%20saber%20sobre%20os%20eventos.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-black uppercase tracking-widest px-6 py-3.5 rounded-xl shadow-lg transition-all"
                    >
                      <i className="fab fa-whatsapp"></i>
                      <span>Contatar Loja via WhatsApp</span>
                    </a>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {storeEvents.length ? (
                    storeEvents.map((ev, i) => (
                      <Link
                        key={i}
                        to={`/evento/${ev.id}`}
                        className="group relative bg-slate-900/50 border border-slate-800 rounded-3xl overflow-hidden hover:border-purple-500/50 transition-all"
                      >
                        <div className="h-40 relative">
                          <img
                            src={
                              ev.imageUrl ||
                              "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400"
                            }
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            alt={ev.name}
                          />
                          <div className="absolute top-4 left-4 bg-slate-950/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
                            <span className="text-[9px] font-black text-white uppercase tracking-widest">
                              {ev.date}
                            </span>
                          </div>
                        </div>
                        <div className="p-6 space-y-3">
                          <h4 className="font-black text-white uppercase tracking-tight">
                            {ev.name}
                          </h4>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-purple-400">
                              {ev.game}
                            </span>
                            <span className="text-sm font-black text-white">
                              R$ {Number(ev.price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="col-span-full py-12 text-center text-slate-500 text-sm italic">
                      Nenhum evento futuro cadastrado.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "products" &&
            (!store.isPartner ? (
              <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">
                <div className="p-8 md:p-12 rounded-[32px] bg-slate-900/30 border border-slate-800/80 text-center flex flex-col items-center justify-center space-y-6">
                  <div className="w-16 h-16 rounded-2xl bg-purple-600/10 text-purple-400 flex items-center justify-center border border-purple-500/10">
                    <i className="fas fa-shield-halved text-3xl"></i>
                  </div>
                  <div className="max-w-md space-y-3">
                    <h4 className="text-base font-black text-white uppercase tracking-wider">
                      Apenas Lojas Parceiras
                    </h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Apenas as lojas parceiras oficiais da Cardumy possuem
                      acesso ao catálogo de produtos no marketplace. Isso
                      garante informações rigorosamente confiáveis, seguras e
                      atualizadas direto do estoque.
                    </p>
                  </div>
                  {store.whatsapp && (
                    <a
                      href={`https://wa.me/${store.whatsapp}?text=Ol%C3%A1!%20Vi%20sua%20loja%20no%20Cardumy%20e%20gostaria%20de%20saber%20sobre%20os%20produtos.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-black uppercase tracking-widest px-6 py-3.5 rounded-xl shadow-lg transition-all"
                    >
                      <i className="fab fa-whatsapp"></i>
                      <span>Contatar Loja via WhatsApp</span>
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {paginatedStoreProducts.length > 0 ? (
                    paginatedStoreProducts.map((p, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          const target = p.slug || p.id;
                          if (target) {
                            navigate(`/produtos/${target}`);
                          }
                        }}
                        className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden flex flex-col group hover:border-purple-500/50 transition-all cursor-pointer"
                      >
                        <div className="aspect-square p-4 bg-slate-950/20 relative flex items-center justify-center overflow-hidden">
                          <img
                            src={p.imageUrl}
                            className="max-h-full max-w-full object-contain rounded-xl group-hover:scale-110 transition-transform duration-700"
                            alt={p.name}
                            referrerPolicy="no-referrer"
                          />
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">
                              {p.type}
                            </p>
                            <h4 className="font-bold text-white text-xs line-clamp-2 h-8 group-hover:text-purple-400 transition-colors">
                              {p.name}
                            </h4>
                          </div>
                          <div className="flex justify-between items-center pt-2">
                            <p className="text-sm font-black text-white">
                              R$ {Number(p.price || 0).toFixed(2)}
                            </p>
                            {(!isLojista || isOwnStore) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (store?.sales_enabled === false) {
                                    showNotification("As vendas estão desabilitadas para esta loja.", "error");
                                    return;
                                  }
                                  onAddToCart(p);
                                }}
                                disabled={store?.sales_enabled === false}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                  store?.sales_enabled === false
                                    ? 'bg-rose-950/20 text-rose-500/50 border border-rose-500/20 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-500 text-white active:scale-90'
                                }`}
                                title={store?.sales_enabled === false ? "Vendas desabilitadas nesta loja" : isOwnStore ? "Adicionar ao carrinho (Venda Balcão)" : "Adicionar ao carrinho"}
                              >
                                <i className={`fas ${store?.sales_enabled === false ? 'fa-ban' : 'fa-plus'} text-xs`}></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-12 text-center text-slate-500 text-sm italic">
                      Esta loja ainda não possui produtos no marketplace.
                    </div>
                  )}
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 pt-8">
                    <button
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:border-purple-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-chevron-left text-xs"></i>
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          return (
                            page === 1 ||
                            page === totalPages ||
                            Math.abs(page - currentPage) <= 1
                          );
                        })
                        .map((page, index, array) => {
                          const isFirst = index === 0;
                          const prevPage = array[index - 1];
                          const showEllipsis = !isFirst && page - prevPage > 1;

                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && (
                                <span className="text-slate-600 px-1">...</span>
                              )}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-xl border font-bold text-xs transition-all ${
                                  currentPage === page
                                    ? "bg-purple-600 border-purple-500 text-white shadow-lg"
                                    : "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700"
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <button
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:border-purple-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-chevron-right text-xs"></i>
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* Info Column */}
        <div className="lg:col-span-4 space-y-6 md:space-y-8">
          {/* Horários e Localização */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-[32px] p-8 space-y-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-600 points-30 to-pink-600"></div>

            <div className="space-y-6">
              <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center">
                <i className="fas fa-clock mr-3 text-purple-500"></i>
                Horários
              </h3>
              <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800/50">
                {store.businessHours && store.businessHours.length > 0 ? (
                  <div className="space-y-2">
                    {DAY_ORDER.map((day, idx) => {
                      const hoursForDay = store.businessHours?.filter(
                        (h) => h.day_of_week === idx,
                      );
                      const isClosed =
                        !hoursForDay ||
                        hoursForDay.length === 0 ||
                        hoursForDay.every((h) => h.is_closed);
                      if (isClosed) return null; // Show only open days in this compact view
                      return (
                        <div
                          key={idx}
                          className="flex justify-between items-center text-[11px]"
                        >
                          <span className="text-slate-500 font-bold">
                            {day}
                          </span>
                          <div className="text-right">
                            {hoursForDay?.map((h, hidx) => (
                              <p key={hidx} className="text-white font-black">
                                {h.open_time?.substring(0, 5) || "--:--"} -{" "}
                                {h.close_time?.substring(0, 5) || "--:--"}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm leading-relaxed whitespace-pre-line">
                    {safeString(store.opening_hours) ||
                      "Horário de funcionamento não informado."}
                  </p>
                )}
              </div>
            </div>

            {(safeString(store.phone) || safeString(store.whatsapp) || safeString(store.email)) && (
              <div className="space-y-4 pt-4 border-t border-slate-800/50">
                <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center">
                  <i className="fas fa-address-book mr-3 text-purple-500"></i>
                  Contato
                </h3>
                <div className="space-y-3 bg-slate-950/50 rounded-2xl p-4 border border-slate-800/50">
                  {safeString(store.phone) && (
                    <div className="flex items-center space-x-3 text-xs text-slate-300">
                      <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shrink-0">
                        <i className="fas fa-phone"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-black uppercase text-slate-500">Telefone</p>
                        <a href={`tel:${safeString(store.phone)!.replace(/\D/g, '')}`} className="font-bold text-white hover:text-purple-400 transition-colors truncate block">
                          {safeString(store.phone)}
                        </a>
                      </div>
                    </div>
                  )}
                  {safeString(store.whatsapp) && (
                    <div className="flex items-center space-x-3 text-xs text-slate-300">
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                        <i className="fab fa-whatsapp"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-black uppercase text-slate-500">WhatsApp</p>
                        <a
                          href={
                            safeString(store.whatsapp)!.replace(/\D/g, '').length === 10 || safeString(store.whatsapp)!.replace(/\D/g, '').length === 11
                              ? `https://wa.me/55${safeString(store.whatsapp)!.replace(/\D/g, '')}`
                              : `https://wa.me/${safeString(store.whatsapp)!.replace(/\D/g, '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-white hover:text-emerald-400 transition-colors truncate block"
                        >
                          {safeString(store.whatsapp)}
                        </a>
                      </div>
                    </div>
                  )}
                  {safeString(store.email) && (
                    <div className="flex items-center space-x-3 text-xs text-slate-300">
                      <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] font-black uppercase text-slate-500">E-mail</p>
                        <a href={`mailto:${safeString(store.email)}`} className="font-bold text-white hover:text-blue-400 transition-colors truncate block">
                          {safeString(store.email)}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-6 pt-4 border-t border-slate-800/50">
              <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center">
                <i className="fas fa-link mr-3 text-purple-500"></i>
                Redes Sociais
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {safeString(store.whatsapp) && (
                  <a
                    href={
                      safeString(store.whatsapp)!.replace(/\D/g, '').length === 10 || safeString(store.whatsapp)!.replace(/\D/g, '').length === 11
                        ? `https://wa.me/55${safeString(store.whatsapp)!.replace(/\D/g, '')}`
                        : `https://wa.me/${safeString(store.whatsapp)!.replace(/\D/g, '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl hover:bg-emerald-500/20 transition-all text-emerald-400"
                  >
                    <i className="fab fa-whatsapp text-lg"></i>
                    <span className="text-[10px] font-black uppercase">
                      Whats
                    </span>
                  </a>
                )}
                {store.instagram && (
                  <a
                    href={formatInstagramUrl(store.instagram)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 bg-pink-500/10 border border-pink-500/20 p-3 rounded-xl hover:bg-pink-500/20 transition-all text-pink-400"
                  >
                    <i className="fab fa-instagram text-lg"></i>
                    <span className="text-[10px] font-black uppercase">
                      Insta
                    </span>
                  </a>
                )}
                {store.discord && (
                  <a
                    href={store.discord}
                    target="_blank"
                    className="flex items-center space-x-3 bg-indigo-500/10 border border-indigo-500/20 p-3 rounded-xl hover:bg-indigo-500/20 transition-all text-indigo-400"
                  >
                    <i className="fab fa-discord text-lg"></i>
                    <span className="text-[10px] font-black uppercase">
                      Discord
                    </span>
                  </a>
                )}
                {store.site && (
                  <a
                    href={store.site}
                    target="_blank"
                    className="flex items-center space-x-3 bg-blue-500/10 border border-blue-500/20 p-3 rounded-xl hover:bg-blue-500/20 transition-all text-blue-400"
                  >
                    <i className="fas fa-globe text-lg"></i>
                    <span className="text-[10px] font-black uppercase">
                      Site
                    </span>
                  </a>
                )}
              </div>
            </div>

            {storeGames.length > 0 && (
              <div className="space-y-4 pt-4 border-t border-slate-800/50">
                <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center">
                  <i className="fas fa-gamepad mr-3 text-purple-500"></i>
                  Jogos Suportados
                </h3>
                <div className="flex flex-wrap gap-2">
                  {storeGames.map((game) => (
                    <span
                      key={game.id}
                      className="bg-purple-500/10 border border-purple-500/20 text-slate-300 text-[10px] font-semibold uppercase tracking-widest px-3 py-1.5 rounded-xl flex items-center"
                    >
                      <i
                        className={`fas ${getGameIconClass(game.name)} text-purple-400 mr-2`}
                      ></i>
                      {game.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
